import { useEffect } from "react";
import { useTheme } from "next-themes";

/** Card colour of the app header (--card): the phone's status bar matches it. */
const THEME_COLOR = { light: "#FFFFFF", dark: "#1F1F1F" } as const;

function upsert(tag: "link" | "meta", key: string, attrs: Record<string, string>): HTMLElement {
  const selector = tag === "link" ? `link[rel="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement(tag);
    el.dataset.appHead = "";
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

/**
 * The install tags, on app pages only (inside AppLayout): the manifest and
 * the iOS home-screen meta. The public site shares index.html, and "Add to
 * Home Screen" there should stay a bookmark of the website, not the app.
 */
export function AppHeadTags() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    upsert("link", "manifest", { rel: "manifest", href: "/app.webmanifest" });
    upsert("meta", "apple-mobile-web-app-capable", {
      name: "apple-mobile-web-app-capable",
      content: "yes",
    });
    upsert("meta", "mobile-web-app-capable", { name: "mobile-web-app-capable", content: "yes" });
    upsert("meta", "apple-mobile-web-app-title", {
      name: "apple-mobile-web-app-title",
      content: "Zettl",
    });
    upsert("meta", "apple-mobile-web-app-status-bar-style", {
      name: "apple-mobile-web-app-status-bar-style",
      content: "default",
    });
  }, []);

  useEffect(() => {
    const meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    const before = meta.content;
    meta.content = resolvedTheme === "light" ? THEME_COLOR.light : THEME_COLOR.dark;
    // Back to the website's colour when leaving the app (previews share the host).
    return () => {
      meta.content = before;
    };
  }, [resolvedTheme]);

  return null;
}
